import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Table, Field, Input, Pill, Empty } from "./ui";
import { toast } from "sonner";
import { Plus, Sparkles, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

export default function HRSettings() {
  const [types, setTypes] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [aiLogs, setAiLogs] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [shiftForm, setShiftForm] = useState(null);
  const [leaveForm, setLeaveForm] = useState(null);

  const load = () => {
    api.get("/hr/shift-types").then(setTypes).catch(() => {});
    api.get("/hr/leave-types").then(setLeaveTypes).catch(() => {});
    api.get("/hr/audit", { params: { limit: 60 } }).then(setAudit).catch(() => {});
    api.get("/hr/ai-logs").then(setAiLogs).catch(() => {});
    api.get("/hr/kiosks").then(setKiosks).catch(() => {});
  };
  useEffect(load, []);

  const saveShift = async () => {
    try {
      if (shiftForm.id) await api.put(`/hr/shift-types/${shiftForm.id}`, shiftForm);
      else await api.post("/hr/shift-types", shiftForm);
      toast.success("Shift type saved"); setShiftForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveLeave = async () => {
    try {
      if (leaveForm.id) await api.put(`/hr/leave-types/${leaveForm.id}`, leaveForm);
      else await api.post("/hr/leave-types", leaveForm);
      toast.success("Leave type saved"); setLeaveForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="hr-settings-page">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <SectionHead title="Shift types" sub="Configurable shift names and working hours"
            right={<Btn className="h-9" data-testid="add-shift-type"
              onClick={() => setShiftForm({ name: "", code: "", start_time: "07:00", end_time: "15:00", color: "#2E7D32" })}><Plus size={14} /> New</Btn>} />
          <Table testid="hr-shift-types" rows={types} empty="No shift types" onRowClick={setShiftForm}
            columns={[
              { key: "name", label: "SHIFT", render: (r) => (
                  <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                    <span className="font-semibold">{r.name}</span></span>) },
              { key: "code", label: "CODE" },
              { key: "start_time", label: "START" },
              { key: "end_time", label: "END" },
            ]} />
        </Card>

        <Card>
          <SectionHead title="Leave types" sub="Entitlement days and paid/unpaid"
            right={<Btn className="h-9" data-testid="add-leave-type"
              onClick={() => setLeaveForm({ name: "", code: "", days_per_year: 0, paid: true })}><Plus size={14} /> New</Btn>} />
          <Table testid="hr-leave-types-settings" rows={leaveTypes} empty="No leave types" onRowClick={setLeaveForm}
            columns={[
              { key: "name", label: "TYPE", render: (r) => <span className="font-semibold">{r.name}</span> },
              { key: "code", label: "CODE" },
              { key: "days_per_year", label: "DAYS / YEAR", align: "right" },
              { key: "paid", label: "PAID", render: (r) => <Pill status={r.paid ? "active" : "draft"} text={r.paid ? "Paid" : "Unpaid"} /> },
            ]} />
        </Card>

        <Card>
          <SectionHead title="Attendance kiosks" sub="Standalone clock-in devices — open the URL on the kiosk tablet" />
          <Table testid="hr-kiosks" rows={kiosks} empty="No kiosks registered"
            columns={[
              { key: "location_name", label: "LOCATION", render: (r) => <span className="font-semibold">{r.location_name}</span> },
              { key: "name", label: "KIOSK" },
              { key: "url", label: "URL", render: (r) => (
                  <a href={r.url} target="_blank" rel="noreferrer" className="font-semibold" style={{ color: HC.burgundy }}>{r.url}</a>) },
              { key: "events_today", label: "EVENTS TODAY", align: "right" },
            ]} />
        </Card>

        <Card>
          <SectionHead title={<span className="flex items-center gap-2"><Sparkles size={16} style={{ color: HC.gold }} /> AI usage log</span>}
            sub="Every workforce intelligence call — user, model, status and estimated cost" />
          {aiLogs.length ? (
            <Table testid="hr-ai-logs" rows={aiLogs.slice(0, 30)} empty="No AI calls yet"
              columns={[
                { key: "timestamp", label: "WHEN", render: (r) => new Date(r.timestamp).toLocaleString("en-NG") },
                { key: "user_name", label: "USER" },
                { key: "feature", label: "FEATURE" },
                { key: "model", label: "MODEL" },
                { key: "duration_ms", label: "MS", align: "right" },
                { key: "estimated_cost_usd", label: "EST. COST", align: "right", render: (r) => `$${(r.estimated_cost_usd || 0).toFixed(5)}` },
                { key: "status", label: "STATUS", render: (r) => <Pill status={r.status === "success" ? "active" : "rejected"} text={r.status} /> },
              ]} />
          ) : <Empty text="No AI calls recorded yet" />}
        </Card>

        <Card>
          <SectionHead title={<span className="flex items-center gap-2"><ShieldCheck size={16} style={{ color: HC.green }} /> HR audit trail</span>}
            sub="Employee, salary, attendance, leave and payroll actions" />
          <Table testid="hr-audit-table" rows={audit.slice(0, 40)} empty="No audit entries"
            columns={[
              { key: "timestamp", label: "WHEN", render: (r) => new Date(r.timestamp).toLocaleString("en-NG") },
              { key: "user_name", label: "USER" },
              { key: "action", label: "ACTION", render: (r) => <Pill status="draft" text={(r.action || "").replace(/_/g, " ")} /> },
              { key: "record", label: "RECORD" },
            ]} />
        </Card>
      </div>

      <Dialog open={!!shiftForm} onOpenChange={(o) => !o && setShiftForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{shiftForm?.id ? "Edit shift type" : "New shift type"}</DialogTitle>
            <DialogDescription>Shift names are configurable — Morning/Afternoon/Evening are only defaults.</DialogDescription>
          </DialogHeader>
          {shiftForm && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" className="col-span-2"><Input data-testid="st-name" value={shiftForm.name} onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })} /></Field>
              <Field label="Code"><Input value={shiftForm.code || ""} onChange={(e) => setShiftForm({ ...shiftForm, code: e.target.value })} /></Field>
              <Field label="Colour"><Input value={shiftForm.color || ""} onChange={(e) => setShiftForm({ ...shiftForm, color: e.target.value })} /></Field>
              <Field label="Start"><Input type="time" data-testid="st-start" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} /></Field>
              <Field label="End"><Input type="time" data-testid="st-end" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setShiftForm(null)}>Cancel</Btn>
            <Btn onClick={saveShift} data-testid="st-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!leaveForm} onOpenChange={(o) => !o && setLeaveForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{leaveForm?.id ? "Edit leave type" : "New leave type"}</DialogTitle>
            <DialogDescription>Entitlements drive the leave balance calculation.</DialogDescription>
          </DialogHeader>
          {leaveForm && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" className="col-span-2"><Input data-testid="lt-name" value={leaveForm.name} onChange={(e) => setLeaveForm({ ...leaveForm, name: e.target.value })} /></Field>
              <Field label="Code"><Input value={leaveForm.code || ""} onChange={(e) => setLeaveForm({ ...leaveForm, code: e.target.value })} /></Field>
              <Field label="Days per year"><Input type="number" data-testid="lt-days" value={leaveForm.days_per_year} onChange={(e) => setLeaveForm({ ...leaveForm, days_per_year: Number(e.target.value) })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setLeaveForm(null)}>Cancel</Btn>
            <Btn onClick={saveLeave} data-testid="lt-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
